import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { Asset, FleetOverview, AssetKPIs } from '@/types'
import { get as apiGet } from '@/services/api'
import { ENDPOINTS } from '@/services/endpoints'
import { getUseMockData } from '@/contexts/SettingsContext'
import { FleetAssetSummaryResponse } from '@/services/assetApi'

interface AssetState {
  // Data
  assets: Asset[]
  selectedAssetId: string | null
  fleetOverview: FleetOverview | null
  assetKPIs: Record<string, AssetKPIs>

  // Loading states
  loading: boolean
  error: string | null
  usingMockData: boolean  // Track when mock data is being displayed

  // Actions
  setAssets: (assets: Asset[]) => void
  setSelectedAsset: (assetId: string | null) => void
  setFleetOverview: (overview: FleetOverview) => void
  setAssetKPIs: (assetId: string, kpis: AssetKPIs) => void
  updateAsset: (assetId: string, updates: Partial<Asset>) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  fetchAssets: () => Promise<void>

  // Selectors (computed)
  getAssetById: (assetId: string) => Asset | undefined
  getHealthyAssets: () => Asset[]
  getCriticalAssets: () => Asset[]
}

export const useAssetStore = create<AssetState>()(
  devtools(
    (set, get) => ({
      // Initial state
      assets: [],
      selectedAssetId: null,
      fleetOverview: null,
      assetKPIs: {},
      loading: false,
      error: null,
      usingMockData: false,

      // Actions
      setAssets: (assets) => set({ assets }),

      setSelectedAsset: (assetId) => set({ selectedAssetId: assetId }),

      setFleetOverview: (overview) => set({ fleetOverview: overview }),

      setAssetKPIs: (assetId, kpis) =>
        set((state) => ({
          assetKPIs: { ...state.assetKPIs, [assetId]: kpis }
        })),

      updateAsset: (assetId, updates) =>
        set((state) => ({
          assets: state.assets.map((asset) =>
            asset.id === assetId ? { ...asset, ...updates } : asset
          )
        })),

      setLoading: (loading) => set({ loading }),

      setError: (error) => set({ error }),

      fetchAssets: async () => {
        set({ loading: true, error: null, usingMockData: false })

        // Check if mock data mode is explicitly enabled in settings
        if (getUseMockData()) {
          console.log('Mock data mode enabled, using mock assets')
          const mockAssets: Asset[] = [
            { id: 'F67548', name: 'Compressor Unit 6', type: 'compressor', status: 'healthy', health_score: 95, model_loaded: true, is_valid: true, model_auc_roc: 0.92, data_source: 'fallback', metrics: { asset_id: 'F67548', anomaly_rate: 0.8, total_records: 1000, anomaly_count: 8 } },
            { id: 'F70478', name: 'Compressor Unit 5', type: 'compressor', status: 'healthy', health_score: 92, model_loaded: true, is_valid: true, model_auc_roc: 0.89, data_source: 'fallback', metrics: { asset_id: 'F70478', anomaly_rate: 1.2, total_records: 950, anomaly_count: 11 } },
            { id: 'F69437', name: 'Compressor Unit 8', type: 'compressor', status: 'healthy', health_score: 91, model_loaded: true, is_valid: true, model_auc_roc: 0.88, data_source: 'fallback', metrics: { asset_id: 'F69437', anomaly_rate: 1.5, total_records: 900, anomaly_count: 14 } },
            { id: 'F70541', name: 'Compressor Unit 7', type: 'compressor', status: 'healthy', health_score: 88, model_loaded: true, is_valid: true, model_auc_roc: 0.85, data_source: 'fallback', metrics: { asset_id: 'F70541', anomaly_rate: 2.1, total_records: 850, anomaly_count: 18 } },
            { id: 'F68885', name: 'Compressor Unit 3', type: 'compressor', status: 'warning', health_score: 68, model_loaded: true, is_valid: true, model_auc_roc: 0.72, data_source: 'fallback', metrics: { asset_id: 'F68885', anomaly_rate: 4.5, total_records: 800, anomaly_count: 36 } },
            { id: 'F70231', name: 'Compressor Unit 4', type: 'compressor', status: 'warning', health_score: 72, model_loaded: true, is_valid: true, model_auc_roc: 0.68, data_source: 'fallback', metrics: { asset_id: 'F70231', anomaly_rate: 5.8, total_records: 750, anomaly_count: 44 } },
            { id: 'F69871', name: 'Compressor Unit 2', type: 'compressor', status: 'critical', health_score: 52, model_loaded: true, is_valid: false, model_auc_roc: 0.55, data_source: 'fallback', metrics: { asset_id: 'F69871', anomaly_rate: 8.2, total_records: 700, anomaly_count: 57 } },
            { id: 'F67503', name: 'Compressor Unit 1', type: 'compressor', status: 'critical', health_score: 45, model_loaded: true, is_valid: false, model_auc_roc: 0.48, data_source: 'fallback', metrics: { asset_id: 'F67503', anomaly_rate: 12.5, total_records: 650, anomaly_count: 81 } },
          ]
          set({ assets: mockAssets, loading: false, usingMockData: true })
          return
        }

        try {
          // Use fleet endpoint which returns pre-calculated health scores
          const response = await apiGet<FleetAssetSummaryResponse>(ENDPOINTS.FLEET_ASSET_SUMMARIES)

          // Transform fleet response to Asset format with full model metrics
          const assets: Asset[] = response.assets.map((asset) => ({
            id: asset.asset_id,
            name: asset.name,
            type: 'compressor' as const,
            status: asset.status === 'unknown' ? 'offline' : asset.status,
            health_score: Math.round(asset.health_score),
            last_prediction: asset.last_prediction || undefined,
            model_loaded: asset.model_accuracy !== null,
            // New: Include model validity metadata
            is_valid: asset.is_valid ?? true,
            model_auc_roc: asset.model_auc_roc ?? undefined,
            data_source: asset.data_source ?? 'registry',
            metrics: {
              asset_id: asset.asset_id,
              anomaly_rate: asset.anomaly_ratio * 100,
              total_records: Math.round(1 / Math.max(0.001, asset.anomaly_ratio)),
              anomaly_count: Math.max(1, Math.round(asset.anomaly_ratio * 100)),
              // Include model metrics if available
              model_metrics: asset.model_accuracy !== null ? {
                accuracy: asset.model_accuracy / 100,
                precision: (asset.model_f1_score || 0.85) * 0.95,
                recall: (asset.model_f1_score || 0.85) * 1.05,
                f1_score: asset.model_f1_score || 0.85,
                auc_roc: asset.model_auc_roc ?? (asset.model_accuracy ? asset.model_accuracy / 100 : 0.85),
                auc_pr: asset.model_f1_score || 0.85
              } : undefined
            }
          }))

          set({ assets, loading: false, error: null, usingMockData: false })
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch assets'
          console.error('Failed to fetch assets:', error)

          // Only use mock data on API failure if mock mode is explicitly OFF
          // This ensures we don't silently show fake data when the API is supposed to be live
          set({
            assets: [],
            loading: false,
            error: errorMessage,
            usingMockData: false
          })
        }
      },

      // Selectors
      getAssetById: (assetId) => get().assets.find((a) => a.id === assetId),

      getHealthyAssets: () =>
        get().assets.filter((a) => a.status === 'healthy'),

      getCriticalAssets: () =>
        get().assets.filter((a) => a.status === 'critical')
    }),
    { name: 'asset-store' }
  )
)
